import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { X, Gift, Loader2, Copy, Check } from "lucide-react";
import { useCart } from "../contexts/CartContext";

const STORAGE_KEY = "sh_wheel_v1";

// Colors cycle for wheel slices
const SLICE_COLORS = ["#E63946", "#FFB703", "#2A9D8F", "#F04856"];

const IconFor = ({ icon, size = 22 }) => {
    // Simple inline SVGs matching the icon key returned from backend
    if (icon === "percent")
        return <span style={{ fontSize: size, fontWeight: 900, lineHeight: 1, fontFamily: "Kanit" }}>%</span>;
    if (icon === "fries")
        return <span style={{ fontSize: size }} role="img" aria-label="fries">🍟</span>;
    if (icon === "drink")
        return <span style={{ fontSize: size }} role="img" aria-label="drink">🥤</span>;
    return <Gift size={size} />;
};

export const SpinWheelPopup = () => {
    const [open, setOpen] = useState(false);
    const [prizes, setPrizes] = useState([]);
    const [spinning, setSpinning] = useState(false);
    const [result, setResult] = useState(null);
    const [rotation, setRotation] = useState(0);
    const [copied, setCopied] = useState(false);
    const shownRef = useRef(false);
    const { setPendingCouponDirect } = useCart();

    // Open after 2s if not already spun
    useEffect(() => {
        if (shownRef.current) return;
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) return;
        const t = setTimeout(() => {
            shownRef.current = true;
            setOpen(true);
        }, 5000);
        return () => clearTimeout(t);
    }, []);

    // Load prize slots
    useEffect(() => {
        if (!open || prizes.length) return;
        api.get("/wheel/prizes").then(({ data }) => setPrizes(data));
    }, [open, prizes.length]);

    const sliceAngle = useMemo(() => (prizes.length ? 360 / prizes.length : 90), [prizes.length]);

    const handleSpin = async () => {
        if (spinning || result) return;
        setSpinning(true);
        try {
            const { data } = await api.post("/wheel/spin");
            const idx = prizes.findIndex((p) => p.id === data.prize_id);
            // Compute rotation: we want the target slice centered at top (pointer at top = -90deg start).
            // Each slice's center is at (idx * sliceAngle + sliceAngle/2) measuring from the right (0deg).
            // To bring it to top, we need angle = -(idx * sliceAngle + sliceAngle/2) + 270 (so it lands under top pointer)
            const target = -(idx * sliceAngle + sliceAngle / 2) + 270;
            const fullSpins = 6 * 360; // several rotations for drama
            setRotation(fullSpins + target);
            setTimeout(() => {
                setResult(data);
                localStorage.setItem(STORAGE_KEY, JSON.stringify({ code: data.code, prize_id: data.prize_id, at: Date.now() }));
                if (setPendingCouponDirect) setPendingCouponDirect(data.code);
                else localStorage.setItem("sh_pending_coupon", data.code);
                setSpinning(false);
            }, 4200);
        } catch {
            setSpinning(false);
        }
    };

    const copy = () => {
        if (!result) return;
        navigator.clipboard?.writeText(result.code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const close = () => setOpen(false);

    if (!open) return null;

    return (
        <div
            data-testid="spin-wheel-popup"
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-up"
            onClick={close}
        >
            <div
                className="relative w-full max-w-md bg-[#16161a] border border-white/10 rounded-sm p-6 sm:p-8 text-center"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={close}
                    data-testid="wheel-close"
                    aria-label="Κλείσιμο"
                    className="absolute top-3 right-3 h-8 w-8 grid place-items-center text-white/60 hover:text-white hover:bg-white/10 rounded-sm"
                >
                    <X size={18} />
                </button>

                {!result ? (
                    <>
                        <div className="inline-flex items-center gap-2 bg-brand-red/10 border border-brand-red/30 text-brand-red px-3 py-1 text-[11px] font-heading uppercase tracking-widest mb-4">
                            <Gift size={12} /> Δώρο καλωσορίσματος
                        </div>
                        <h2 className="headline text-3xl sm:text-4xl">Γύρνα τον τροχό!</h2>
                        <p className="text-sm text-white/60 mt-2 mb-6">Κέρδισε έκπτωση ή δώρο στην πρώτη σου παραγγελία.</p>

                        <div className="relative mx-auto w-64 h-64 sm:w-72 sm:h-72 select-none">
                            {/* Pointer */}
                            <div className="absolute left-1/2 -translate-x-1/2 -top-2 z-10 pointer-events-none">
                                <div className="w-0 h-0" style={{
                                    borderLeft: "12px solid transparent",
                                    borderRight: "12px solid transparent",
                                    borderTop: "18px solid #FFB703",
                                    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.5))",
                                }} />
                            </div>

                            {/* Wheel */}
                            <div
                                data-testid="wheel-disc"
                                className="absolute inset-0 rounded-full border-4 border-white/20 overflow-hidden shadow-[0_0_40px_rgba(230,57,70,0.3)]"
                                style={{
                                    transform: `rotate(${rotation}deg)`,
                                    transition: spinning ? "transform 4s cubic-bezier(0.17, 0.67, 0.2, 1)" : "none",
                                    background: prizes.length
                                        ? `conic-gradient(${prizes.map((_, i) =>
                                              `${SLICE_COLORS[i % SLICE_COLORS.length]} ${i * sliceAngle}deg ${(i + 1) * sliceAngle}deg`
                                          ).join(",")})`
                                        : "#0c0c0e",
                                }}
                            >
                                {prizes.map((p, i) => {
                                    // Label positioning: middle of each slice at radius ~38%
                                    const angle = i * sliceAngle + sliceAngle / 2;
                                    return (
                                        <div
                                            key={p.id}
                                            className="absolute left-1/2 top-1/2 origin-left text-white font-heading uppercase"
                                            style={{
                                                transform: `rotate(${angle}deg) translate(50%, -50%)`,
                                                width: "50%",
                                                paddingRight: 16,
                                                textAlign: "right",
                                                fontSize: 12,
                                                letterSpacing: "0.02em",
                                                textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                                            }}
                                        >
                                            <span className="inline-flex items-center gap-1.5" style={{ transform: "rotate(0deg)" }}>
                                                <IconFor icon={p.icon} size={18} />
                                                {p.label}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Center hub */}
                            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-brand-yellow border-4 border-[#0c0c0e] z-10" />
                        </div>

                        <button
                            data-testid="wheel-spin-btn"
                            onClick={handleSpin}
                            disabled={spinning || !prizes.length}
                            className="btn-primary w-full mt-8 flex items-center justify-center gap-2"
                        >
                            {spinning ? (<><Loader2 className="animate-spin" size={16} /> Γυρίζει...</>) : "Γύρνα τον τροχό"}
                        </button>
                        <p className="text-[11px] text-white/40 mt-3">Μία φορά ανά πελάτη.</p>
                    </>
                ) : (
                    <div data-testid="wheel-result" className="animate-fade-up">
                        <div className="text-6xl mb-3">🎉</div>
                        <h2 className="headline text-3xl">Κέρδισες!</h2>
                        <p className="text-brand-yellow font-heading uppercase tracking-wider text-lg mt-2">{result.label}</p>

                        <div className="mt-6 p-4 border-2 border-dashed border-brand-yellow/50 bg-brand-yellow/5">
                            <p className="text-[11px] uppercase tracking-widest text-white/50 mb-1">Ο κωδικός σου</p>
                            <div className="flex items-center justify-center gap-3">
                                <span data-testid="wheel-code" className="font-mono text-xl font-bold text-brand-yellow">{result.code}</span>
                                <button onClick={copy} className="h-8 w-8 grid place-items-center border border-white/15 hover:border-brand-yellow" aria-label="Αντιγραφή">
                                    {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                                </button>
                            </div>
                        </div>

                        <p className="text-xs text-white/60 mt-4 leading-relaxed">
                            {result.min_order > 0 && `Ελάχιστη παραγγελία €${result.min_order.toFixed(2)}. `}
                            Εφαρμόζεται αυτόματα στο checkout.
                        </p>

                        <button
                            data-testid="wheel-start-order"
                            onClick={() => { close(); window.location.href = "/menu"; }}
                            className="btn-primary w-full mt-6"
                        >
                            Παρήγγειλε τώρα
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
